import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { isWeekend, getNextBusinessDay } from "../../utils/dateValidation";
import {
  FiGrid,
  FiCalendar,
  FiUser,
  FiLogOut,
  FiChevronDown,
  FiPlus,
  FiChevronLeft,
  FiChevronRight,
  FiMenu,
  FiX,
} from "react-icons/fi";
import logoName from "../../images/auravit_logo.png";
import auravitIcon from "../../images/auraviticon.png";
import cfsiLogo from "../../images/CFSI-mobile-logo-sr.png";

import { HiOutlineSwitchVertical } from "react-icons/hi";
import { MdOutlineUpcoming, MdHistory, MdNotificationsNone, MdAssignment, MdDescription } from "react-icons/md";

import MedicalDashboard from "./MedicalDashboard";
import ReviewAppointments from "./ReviewAppointments";
import UpcomingAppointments from "./UpcomingAppointments";
import AppointmentHistory from "./AppointmentHistory";
import MedicalRecords from "./MedicalRecords";
import MedicalProfile from "./MedicalProfile";
import ProNotifications from "./ProNotifications";
import MedicalEscalatedCases from "./MedicalEscalatedCases";
import StaffLogoutModal from "./StaffLogoutModal";
import { useLanguage } from "../../contexts/LanguageContext";
import "./staffcss/medicalprofessional.css";
import { db, auth } from "../../firebase";
import { collection, query, where, onSnapshot, getDoc, doc, orderBy, limit } from "firebase/firestore";
import { appointmentService } from "../../services/appointmentService";
import { notificationService } from "../../services/firestoreService";
import { switchRoleWithUpdate, roleConfigs } from "../../utils/roleSwitching";
import StaffChangePassword from "../../components/staffcomponents/StaffChangePassword";
import StaffChangeVerificationModal from "../../components/otpmodals/StaffChangeVerificationModal";
import { generateDynamicSlots, getServiceRule, formatDateToPH, getPHNow, getSlotValidation } from "../../utils/appointmentUtils";

// ----- Helper Functions -----

function NotificationModal({ message, type, onClose }) {
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        onClose();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message, onClose]);

  if (!message) return null;

  return createPortal(
    <div
      className={`feedback-modal ${type}`}
      style={{
        position: "fixed",
        top: "20px",
        right: "20px",
        padding: "12px 20px",
        borderRadius: "8px",
        color: "#fff",
        backgroundColor: type === "success" ? "#36C667" : "#e74c3c",
        boxShadow: "0 4px 6px rgba(0,0,0,0.2)",
        zIndex: 20000, // Higher than any modal
        transition: "opacity 0.3s",
      }}
    >
      {message}
    </div>,
    document.body
  );
}

const generateAppointmentReference = (appointmentType) => {
  const now = new Date();
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Manila',
    year: '2-digit',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(now).reduce((acc, part) => {
    if (part.type === 'year') acc.year = part.value;
    if (part.type === 'month') acc.month = part.value;
    if (part.type === 'day') acc.day = part.value;
    return acc;
  }, { year: '', month: '', day: '' });

  const randomDigits = () => String(Math.floor(Math.random() * 10000)).padStart(4, '0');
  const prefix = appointmentType === 'walk-in' ? 'AW' : 'AS';
  return `${prefix}-${parts.year}${parts.month}${parts.day}-${randomDigits()}`;
};

const WalkinModal = ({ onClose, onCreated, setNotification }) => {
  const [formData, setFormData] = useState({
    patientName: "",
    clinicId: "park-ave",
    clinicName: "Park Avenue Clinic Branch",
    serviceId: "rh_consult",
    serviceName: "Reproductive Health Consultation",
    appointmentType: ""
  });
  
  const [selectedDate, setSelectedDate] = useState(getNextBusinessDay(new Date()));
  const [selectedTime, setSelectedTime] = useState("");
  const [bookedSlots, setBookedSlots] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Dynamic Time Slots
  const [timeSlots, setTimeSlots] = useState({ morning: [], afternoon: [] });

  const services = [
    { id: "rh_consult", name: "Reproductive Health Consultation" },
    { id: "family_planning", name: "Family Planning / Contraceptive Services" },
    { id: "sti_testing", name: "STI Testing / Screening" }
  ];

  const appointmentTypeOptions = [
    { value: "scheduled", label: "Appointment Scheduled (Online)" },
    { value: "walk-in", label: "Appointment Walk-in" }
  ];

  useEffect(() => {
    // Prevent time slot generation on weekends
    if (isWeekend(selectedDate)) {
      setTimeSlots({ morning: [], afternoon: [] });
      setSelectedTime("");
      return;
    }
    const slots = generateDynamicSlots(formData.serviceId);
    setTimeSlots(slots);
    // Reset selected time if it's no longer in the new slots
    setSelectedTime("");
  }, [formData.serviceId, formData.appointmentType, selectedDate]);

  useEffect(() => {
    const fetchBookedSlots = async () => {
      if (!selectedDate) return;
      try {
        const dateStr = formatDateToPH(selectedDate);
        const slots = await appointmentService.getBookedSlots(dateStr);
        setBookedSlots(slots);
      } catch (err) {
        console.error("Error fetching booked slots:", err);
      }
    };
    fetchBookedSlots();
  }, [selectedDate]);

  // Ensure selectedDate is never a weekend (safety net)
  useEffect(() => {
    if (isWeekend(selectedDate)) {
      const nextBusinessDay = getNextBusinessDay(selectedDate);
      setSelectedDate(nextBusinessDay);
      setNotification({ message: "The clinic is closed on weekends. Automatically selected the next available business day.", type: "error" });
    }
  }, [selectedDate, setNotification]);

  const validateName = (name) => {
    if (!name) return "Patient Name is required.";
    if (name.length > 50) return "Max 50 characters allowed.";
    const nameRegex = /^[a-zA-Z\s\-']+$/;
    if (!nameRegex.test(name)) return "Numbers and special characters (except - and ') are not allowed.";
    return null;
  };

  const isTimeSlotValid = (time) => {
    const rule = getServiceRule(formData.serviceId);
    const bookingType = formData.appointmentType === "walk-in" ? "WALK_IN" : "ONLINE";

    const conflict = bookedSlots.some(slot => {
      if (slot.time !== time) return false;
      
      const sameService = slot.serviceId === formData.serviceId || slot.serviceName === formData.serviceName;
      if (!sameService) return false;

      if (rule && rule.separateBookingType === false) {
        return true; // 1 TOTAL
      } else {
        return slot.bookingType === bookingType; // 1 ONLINE + 1 WALK_IN
      }
    });

    if (conflict) return { valid: false, reason: "booked" };

    const slotValidation = getSlotValidation(time, formData.serviceId);
    if (!slotValidation.valid) {
      return { valid: false, reason: slotValidation.reason === "lunch" ? "lunch" : "service" };
    }

    const phNow = getPHNow();
    const [timeStr, period] = time.split(' ');
    let [hours, minutes] = timeStr.split(':').map(Number);
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;

    const selectedDateStr = formatDateToPH(selectedDate);
    const todayPHStr = formatDateToPH(new Date());

    const isToday = selectedDateStr === todayPHStr;
    const isPastDate = selectedDateStr < todayPHStr;

    if (isPastDate) return { valid: false, reason: "past" };
    if (isToday) {
      if (hours < phNow.hour) return { valid: false, reason: "past" };
      if (hours === phNow.hour && minutes <= phNow.minute) return { valid: false, reason: "past" };
    }
    return { valid: true };
  };

  const handleSelectTime = (time) => {
    const { valid, reason } = isTimeSlotValid(time);
    if (!valid) {
      if (reason === "past") {
        setNotification({ message: "This time slot has already passed. Please choose a future time.", type: "error" });
      } else if (reason === "booked") {
        setNotification({ message: "This time slot is no longer available. Please select another.", type: "error" });
      }
      return;
    }
    setSelectedTime(time);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nameError = validateName(formData.patientName);
    if (nameError) {
      setNotification({ message: nameError, type: "error" });
      return;
    }
    if (!formData.appointmentType) {
      setNotification({ message: "Please choose an appointment type.", type: "error" });
      return;
    }
    if (!selectedTime) {
      setNotification({ message: "Please select a time slot.", type: "error" });
      return;
    }
    if (!selectedDate) {
      setNotification({ message: "Please select a date.", type: "error" });
      return;
    }

    if (isWeekend(selectedDate)) {
      setNotification({ message: "The clinic is closed on weekends.\nPlease select an available weekday.", type: "error" });
      return;
    }

    setIsSubmitting(true);
    try {
      const user = auth.currentUser;
      const isWalkIn = formData.appointmentType === "walk-in";
      const bookingType = isWalkIn ? "WALK_IN" : "ONLINE";
      const apptData = {
        clinicId: formData.clinicId,
        clinicName: formData.clinicName,
        date: formatDateToPH(selectedDate),
        patientName: formData.patientName,
        patientDob: "walk-in",
        proId: user.uid,
        proName: user.displayName || "Medical Professional",
        reference: generateAppointmentReference(formData.appointmentType),
        serviceId: formData.serviceId,
        serviceName: formData.serviceName,
        status: "CONFIRMED",
        time: selectedTime,
        type: isWalkIn ? "walk-in" : "scheduled",
        bookingType,
        appointmentType: formData.appointmentType,
        uid: "walk-in"
      };
      const result = await appointmentService.createAppointment(apptData);
      onCreated(result);
    } catch (err) {
      setNotification({ message: "Failed to create walk-in: " + err.message, type: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const todayPHStr = formatDateToPH(new Date());
  const daysOfWeek = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  const getCalendarDays = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startOffset = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
    const totalDays = lastDay.getDate();
    const cells = [];
    for (let i = 0; i < startOffset; i++) cells.push(null);
    for (let day = 1; day <= totalDays; day++) cells.push(day);
    return cells;
  };

  const calendarDays = getCalendarDays(selectedDate);

  return createPortal(
    <>

      <div className="modal-backdrop" style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0, 
        backgroundColor: 'rgba(0, 0, 0, 0.5)', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        zIndex: 10000 
      }}>
        <div 
          style={{ 
            maxWidth: '680px', 
            width: '95%', 
            maxHeight: '90vh', 
            display: 'flex', 
            flexDirection: 'column',
            borderRadius: '20px',
            backgroundColor: '#fff',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            overflow: 'hidden',
            border: '1px solid rgba(0,0,0,0.05)'
          }}
        >
        {/* Header - Fixed */}
        <div style={{ 
          background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
          width: '100%',
          padding: '20px 28px', 
          borderBottom: '1px solid rgba(37, 99, 235, 0.2)', 
          display: 'flex', 
          justifyContent: 'flex-start', 
          alignItems: 'center',
          flexShrink: 0,
          boxSizing: 'border-box',
          gap: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          minHeight: 'auto',
          overflow: 'visible'
        }}>
          <div style={{ 
            background: 'rgba(255,255,255,0.2)', 
            borderRadius: '10px', 
            padding: '8px', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center'
          }}>
            <span style={{ fontSize: '18px' }}>📅</span>
          </div>
          <h3 style={{ 
            margin: 0, 
            fontSize: '18px', 
            fontWeight: 700, 
            color: '#ffffff', 
            background: 'none', 
            backgroundImage: 'none', 
            opacity: 1, 
            visibility: 'visible', 
            display: 'block', 
            transform: 'none',
            letterSpacing: '-0.01em'
          }}>Create Appointment</h3>
        </div>
        
        {/* Body - Scrollable */}
        <div 
          style={{ 
            overflowY: 'auto', 
            padding: '28px', 
            flex: 1,
            background: '#fafbfc',
            minHeight: 0, // Crucial for flex scroll
            width: '100%',
            boxSizing: 'border-box'
          }}
        >
          <form id="walkin-form" onSubmit={handleSubmit}>
            <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px' }}>
              {/* Left Column: Patient & Service Info */}
              <div className="walkin-left-col" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                <div className="form-group">
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#4b5563' }}>Patient Name</label>
                  <input 
                    type="text" 
                    placeholder="e.g. Juan Dela Cruz"
                    required 
                    style={{ 
                      width: '100%', 
                      padding: '12px 16px', 
                      borderRadius: '8px', 
                      border: '1px solid #d1d5db',
                      fontSize: '15px',
                      cursor: 'text',
                      outline: 'none',
                      transition: 'border-color 0.2s'
                    }}
                    value={formData.patientName} 
                    onChange={e => setFormData({...formData, patientName: e.target.value})} 
                  />
                </div>
                <div className="form-group">
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#4b5563' }}>Appointment Type</label>
                  <select 
                    required
                    style={{ 
                      width: '100%', 
                      padding: '12px 16px', 
                      borderRadius: '8px', 
                      border: '1px solid #d1d5db',
                      fontSize: '15px',
                      backgroundColor: '#fff',
                      cursor: 'pointer',
                      outline: 'none'
                    }}
                    value={formData.appointmentType} 
                    onChange={e => {
                      setFormData({ ...formData, appointmentType: e.target.value });
                      setSelectedTime("");
                    }}
                  >
                    <option value="">Select appointment type</option>
                    {appointmentTypeOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#4b5563' }}>Clinic</label>
                  <input 
                    type="text" 
                    value="Park Avenue Clinic Branch" 
                    readOnly 
                    style={{ 
                      width: '100%', 
                      padding: '12px 16px', 
                      borderRadius: '8px', 
                      border: '1px solid #e5e7eb',
                      backgroundColor: '#f9fafb',
                      color: '#6b7280',
                      fontSize: '15px',
                      cursor: 'not-allowed'
                    }} 
                  />
                </div>
                <div className="form-group">
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#4b5563' }}>Service</label>
                  <select 
                    style={{ 
                      width: '100%', 
                      padding: '12px 16px', 
                      borderRadius: '8px', 
                      border: '1px solid #d1d5db',
                      fontSize: '15px',
                      backgroundColor: '#fff',
                      cursor: 'pointer',
                      outline: 'none'
                    }}
                    value={formData.serviceId} 
                    onChange={e => setFormData({
                      ...formData, 
                      serviceId: e.target.value, 
                      serviceName: services.find(s => s.id === e.target.value).name
                    })}
                  >
                    {services.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                  <div style={{ marginTop: '12px', display: 'flex', gap: '20px', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ width: '8px', height: '8px', backgroundColor: '#19458c', borderRadius: '50%' }} />
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>Scheduled</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ width: '8px', height: '8px', backgroundColor: '#fdebb2', borderRadius: '50%' }} />
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>Walk-in</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Date & Time selection (under legend) */}
              <div style={{ width: '100%' }}>
                <div style={{ background: '#fff', padding: '24px', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', width: '100%', boxSizing: 'border-box' }}>
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                      <button type="button" onClick={() => {
                        const d = new Date(selectedDate);
                        d.setMonth(d.getMonth() - 1);
                        setSelectedDate(d);
                      }} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', width: '32px', height: '32px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>‹</button>
                      <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#1e293b', letterSpacing: '0.025em' }}>{selectedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }).toUpperCase()}</h4>
                      <button type="button" onClick={() => {
                        const d = new Date(selectedDate);
                        d.setMonth(d.getMonth() + 1);
                        setSelectedDate(d);
                      }} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', width: '32px', height: '32px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>›</button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', textAlign: 'center' }}>
                      {daysOfWeek.map(d => <div key={d} style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8', paddingBottom: '8px', textTransform: 'uppercase' }}>{d}</div>)}
                      {calendarDays.map((day, idx) => {
                        if (!day) return <div key={idx}></div>;
                        const curDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), day);
                        const curDateStr = formatDateToPH(curDate);
                        const isPast = curDateStr < todayPHStr;
                        const isWeekendDate = isWeekend(curDate);
                        const isDisabled = isPast || isWeekendDate;
                        const isSelected =
                          selectedDate.getDate() === curDate.getDate() &&
                          selectedDate.getMonth() === curDate.getMonth() &&
                          selectedDate.getFullYear() === curDate.getFullYear();
                        return (
                          <button
                            key={idx}
                            type="button"
                            disabled={isDisabled}
                            title={isWeekendDate ? 'Clinic closed on weekends.' : ''}
                            style={{
                              padding: '8px 0',
                              borderRadius: '10px',
                              border: 'none',
                              background: isSelected ? '#29499d' : 'transparent',
                              color: isSelected ? '#fff' : (isDisabled ? '#cbd5e1' : '#475569'),
                              cursor: isDisabled ? 'not-allowed' : 'pointer',
                              fontSize: '13px',
                              fontWeight: isSelected ? 700 : 500,
                              transition: 'all 0.2s',
                              boxShadow: isSelected ? '0 2px 8px rgba(59, 130, 246, 0.35)' : 'none'
                            }}
                            onClick={() => {
                              if (isWeekendDate) {
                                setNotification({ message: "The clinic is closed on weekends.\nPlease select an available weekday.", type: "error" });
                                return;
                              }
                              if (isPast) return;
                              setSelectedDate(curDate);
                            }}
                          >{day}</button>
                        );
                      })}
                    </div>
                  </div>

                  <div style={{ marginTop: '24px', borderTop: '1px solid #f1f5f9', paddingTop: '20px' }}>
                    <h5 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600, color: '#1e293b' }}>Select Time</h5>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                      {[...timeSlots.morning, ...timeSlots.afternoon].map(time => {
                        const { valid, reason } = isTimeSlotValid(time);
                        const isSel = selectedTime === time;
                        // Find ALL booked slots for this time and service
                        const matchingSlots = bookedSlots.filter(slot => {
                          const sameService = slot.serviceId === formData.serviceId || slot.serviceName === formData.serviceName;
                          const sameTime = slot.time === time;
                          return sameService && sameTime;
                        });
                        // Get unique booking types to avoid duplicate dots
                        const uniqueBookingTypes = [...new Set(matchingSlots.map(slot => slot.bookingType))];
                        
                        // Check if the slot is fully booked or past
                        const isFullyBooked = !valid && reason === "booked";
                        const isPastHour = !valid && reason === "past";
                        
                        let hoverText = "";
                        if (isFullyBooked) hoverText = "Date fully booked";
                        if (isPastHour) hoverText = "Hour has already passed";
                        
                        return (
                          <button
                            key={time}
                            type="button"
                            style={{
                              padding: '8px 2px 16px 2px',
                              fontSize: '11px',
                              borderRadius: '10px',
                              border: '1px solid',
                              borderColor: isSel ? '#3f62b5' : (valid ? '#e2e8f0' : '#f1f5f9'),
                              background: isSel ? 'linear-gradient(135deg, #3f62b5 0%, #2563eb 100%)' : (valid ? '#fff' : '#f8fafc'),
                              color: isSel ? '#fff' : (valid ? '#475569' : '#9ca3af'),
                              cursor: valid ? 'pointer' : 'default',
                              fontWeight: isSel ? 700 : 500,
                              transition: 'all 0.2s',
                              position: 'relative',
                              boxShadow: isSel ? '0 2px 8px rgba(59, 130, 246, 0.35)' : 'none'
                            }}
                            onMouseEnter={(e) => { if (valid && !isSel) { e.target.style.background = '#eff6ff'; e.target.style.borderColor = '#3f62b5'; } }}
                            onMouseLeave={(e) => { if (valid && !isSel) { e.target.style.background = '#fff'; e.target.style.borderColor = '#e2e8f0'; } }}
                            onClick={() => handleSelectTime(time)}
                            title={hoverText}
                          >
                            {time}
                            {uniqueBookingTypes.length > 0 && (
                              <div style={{ 
                                position: 'absolute', 
                                bottom: '4px', 
                                left: '50%', 
                                transform: 'translateX(-50%)', 
                                display: 'flex', 
                                gap: '4px',
                                alignItems: 'center'
                              }}>
                                {uniqueBookingTypes.map((bookingType, idx) => (
                                  <div 
                                    key={idx}
                                    style={{ 
                                      width: '8px', 
                                      height: '8px', 
                                      backgroundColor: bookingType === "WALK_IN" ? '#fdebb2' : '#3f62b5', 
                                      borderRadius: '50%' 
                                    }} 
                                  />
                                ))}
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Footer - Fixed */}
        <div style={{ 
          padding: '20px 28px', 
          borderTop: '1px solid #f1f5f9', 
          display: 'flex', 
          justifyContent: 'flex-end', 
          gap: '14px', 
          background: '#fff',
          flexShrink: 0
        }}>
          <button 
            type="button" 
            onClick={onClose} 
            style={{ 
              padding: '11px 22px', 
              borderRadius: '12px', 
              border: '1px solid #e2e8f0', 
              background: '#fff', 
              color: '#4b5563', 
              fontWeight: 700, 
              cursor: 'pointer', 
              transition: 'all 0.2s',
              fontSize: '14px'
            }}
            onMouseEnter={(e) => { e.target.style.background = '#f8fafc'; e.target.style.borderColor = '#cbd5e1'; }}
            onMouseLeave={(e) => { e.target.style.background = '#fff'; e.target.style.borderColor = '#e2e8f0'; }}
          >
            Cancel
          </button>
          <button 
            type="button" 
            disabled={isSubmitting} 
            onClick={handleSubmit}
            style={{ 
              padding: '11px 22px', 
              borderRadius: '12px', 
              border: 'none', 
              background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)', 
              color: '#fff', 
              fontWeight: 700, 
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              opacity: isSubmitting ? 0.7 : 1,
              transition: 'all 0.2s',
              boxShadow: isSubmitting ? 'none' : '0 4px 14px rgba(37, 99, 235, 0.4)',
              fontSize: '14px'
            }}
            onMouseEnter={(e) => { if (!isSubmitting) { e.target.style.transform = 'translateY(-2px)'; e.target.style.boxShadow = '0 6px 20px rgba(37, 99, 235, 0.5)'; } }}
            onMouseLeave={(e) => { if (!isSubmitting) { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = '0 4px 14px rgba(37, 99, 235, 0.4)'; } }}
          >
            {isSubmitting ? "Creating..." : "Create Appointment"}
          </button>
        </div>
      </div>
    </div>
    </>,
    document.body
  );
};

const NAV_CONFIG = {
  dashboard: {
    sidebarLabel: "Dashboard",
    topTitle: "Dashboard"
  },
  review: {
    sidebarLabel: "Review Appointments",
    topTitle: "Review Appointments"
  },
  upcoming: {
    sidebarLabel: "Upcoming Appointments",
    topTitle: "Upcoming Appointments"
  },
  history: {
    sidebarLabel: "Appointment History",
    topTitle: "Appointment History"
  },
  notifications: {
    sidebarLabel: "Notifications",
    topTitle: "Notifications"
  },
  escalatedCases: {
    sidebarLabel: "Escalated Cases",
    topTitle: "Escalated Cases"
  },
  profile: {
    sidebarLabel: "Profile",
    topTitle: "Profile"
  }
};

const MedicalProfessional = () => {
  const { getTranslations } = useLanguage();
  const t = getTranslations(false, false, false, true);
  
  const NAV_CONFIG = {
    dashboard: {
      sidebarLabel: t.dashboard,
      topTitle: t.dashboard
    },
    review: {
      sidebarLabel: t.reviewAppointments,
      topTitle: t.reviewAppointments
    },
    upcoming: {
      sidebarLabel: "Upcoming Appointments",
      topTitle: "Upcoming Appointments"
    },
    history: {
      sidebarLabel: "Appointment History",
      topTitle: "Appointment History"
    },
    records: {
      sidebarLabel: "Records",
      topTitle: "Records"
    },
    notifications: {
      sidebarLabel: "Notifications",
      topTitle: "Notifications"
    },
    escalatedCases: {
      sidebarLabel: t.escalatedCases,
      topTitle: t.escalatedCases
    },
    profile: {
      sidebarLabel: t.profile,
      topTitle: t.profile
    }
  };
  
  const navigate = useNavigate();
  const location = useLocation();
  const [activePanel, setActivePanel] = useState("dashboard");
  const [highlightState, setHighlightState] = useState(null); // { highlightPatientUid, highlightAppointmentId }
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const [tooltip, setTooltip] = useState({ visible: false, text: '', x: 0, y: 0 });
  const [reviewAppointments, setReviewAppointments] = useState([]);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [historyAppointments, setHistoryAppointments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
  const [staffProfile, setStaffProfile] = useState(null);
  const [canReviewAppointments, setCanReviewAppointments] = useState(false);
  const [showPasswordChangeModal, setShowPasswordChangeModal] = useState(false);
  const [showPasswordVerificationModal, setShowPasswordVerificationModal] = useState(false);
  const [pendingPasswordChange, setPendingPasswordChange] = useState(null);
  const [userData, setUserData] = useState(null);

  // Walk-in modal state
  const [showWalkinModal, setShowWalkinModal] = useState(false);
  const [notification, setNotification] = useState({ message: "", type: "" });
  
  // Switch account dropdown state
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const [currentUserRole, setCurrentUserRole] = useState("");
  const [availableRoles, setAvailableRoles] = useState([]);
  const [userRoles, setUserRoles] = useState([]);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const switchButtonRef = useRef(null);

  // Handle role switching
  const handleRoleSwitch = (selectedRole) => {
    const switched = switchRoleWithUpdate(selectedRole.id, currentUserRole, setCurrentUserRole, navigate);
    if (switched) {
      setShowRoleDropdown(false);
    }
  };

  // Calculate dropdown position for collapsed sidebar
  const handleDropdownToggle = () => {
    if (!showRoleDropdown && sidebarCollapsed && isDesktop && switchButtonRef.current) {
      const rect = switchButtonRef.current.getBoundingClientRect();
      const dropdownWidth = 200; // estimated width
      const dropdownHeight = 150; // estimated height
      
      // Calculate position to center dropdown above button
      let top = rect.top - dropdownHeight - 8;
      let left = rect.left + (rect.width / 2) - (dropdownWidth / 2);
      
      // Ensure dropdown doesn't go off-screen horizontally
      if (left < 10) left = 10;
      if (left + dropdownWidth > window.innerWidth - 10) {
        left = window.innerWidth - dropdownWidth - 10;
      }
      
      // Ensure dropdown doesn't go off-screen vertically
      if (top < 10) top = rect.bottom + 8; // Show below if above would be off-screen
      
      setDropdownPosition({ top, left });
    }
    setShowRoleDropdown(!showRoleDropdown);
  };

  // Desktop detection
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return undefined;

    const unsubscribe = notificationService.getUserNotificationsListener(user.uid, (notifDocs) => {
      const notifs = notifDocs.map((n) => {
        const accessStatus = n.accessStatus || n.access_status || n.eventStatus || null;
        const statusLabel = accessStatus || (n.type === 'data_access_termination' ? 'EXPIRED' : '');
        const readStatus = n.status || 'unread';
        const createdAt = n.createdAt || new Date();
        const dateValue = createdAt instanceof Date ? createdAt : new Date(createdAt);

        return {
          id: n.id,
          type: n.type || 'data_access_request',
          status: statusLabel,
          readStatus,
          title: n.notificationsTitle || (statusLabel === 'ACTIVE' ? 'Access Approved' : (statusLabel === 'DENIED' ? 'Access Denied' : (statusLabel === 'EXPIRED' ? 'Access Expired' : 'Access Update'))),
          desc: n.message || '',
          timestamp: dateValue,
          time: dateValue.toLocaleString(),
          patientUid: n.patientUid,
          appointmentId: n.appointmentId
        };
      });

      notifs.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
      setNotifications(notifs);
      setUnreadNotificationsCount(notifs.filter((n) => n.readStatus === 'unread').length);
    });

    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, []);

  // Handle highlight state from ProNotifications navigation
  useEffect(() => {
    if (location.state?.highlightPatientUid) {
      setHighlightState({
        highlightPatientUid: location.state.highlightPatientUid,
        highlightAppointmentId: location.state.highlightAppointmentId || null
      });
      setActivePanel('records');

      // Clear location state to prevent re-triggering
      window.history.replaceState({}, document.title);

      // Auto-clear highlight after 5 seconds
      const timer = setTimeout(() => {
        setHighlightState(null);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [location.state]);

  // Tooltip handlers
  const handleMouseEnter = (e, text) => {
    if (isDesktop && sidebarCollapsed) {
      const tooltipX = e.clientX + 12;
      const tooltipY = e.clientY;
      
      // Prevent tooltip from causing scroll by ensuring it stays within viewport
      const tooltipHeight = 40; // estimated height
      const viewportHeight = window.innerHeight;
      const adjustedY = Math.min(tooltipY, viewportHeight - tooltipHeight - 10);
      
      setTooltip({
        visible: true,
        text: text,
        x: tooltipX,
        y: adjustedY
      });
    }
  };

  const handleMouseMove = (e) => {
    if (tooltip.visible && isDesktop && sidebarCollapsed) {
      const tooltipX = e.clientX + 12;
      const tooltipY = e.clientY;
      
      // Prevent tooltip from causing scroll by ensuring it stays within viewport
      const tooltipHeight = 40; // estimated height
      const viewportHeight = window.innerHeight;
      const adjustedY = Math.min(tooltipY, viewportHeight - tooltipHeight - 10);
      
      setTooltip(prev => ({
        ...prev,
        x: tooltipX,
        y: adjustedY
      }));
    }
  };

  const handleMouseLeave = () => {
    setTooltip({ visible: false, text: '', x: 0, y: 0 });
  };

  const handlePasswordVerificationRequired = (passwordData) => {
    setPendingPasswordChange(passwordData);
    setShowPasswordChangeModal(false); // Close password change modal
    setShowPasswordVerificationModal(true);
  };

  const handlePasswordVerificationSuccess = async (result) => {
    setShowPasswordVerificationModal(false);
    // Password change was successful, user will be signed out by the modal
    // Navigate to login page
    navigate('/login');
  };

  const handlePasswordVerificationCancel = () => {
    setShowPasswordVerificationModal(false);
    setShowPasswordChangeModal(true); // Reopen password change modal
  };
  
  // Fetch user roles from Firestore
  useEffect(() => {
    const fetchUserRoles = async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          // Retry after 1 second if no user is found
          setTimeout(fetchUserRoles, 1000);
          return;
        }
        
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUserData(userData);
          let roles = userData.roles || [];

          // Add Medical Professional role for users who are explicitly set or qualified by assigned services
          if (userData.role === "Medical Professional" && !roles.includes("Medical Professional")) {
            roles = [...roles, "Medical Professional"];
          }
          if (roles.includes("Peer Educator") && 
              (userData.servicesAssigned === true || userData.servicesassigned === true) && 
              !roles.includes("Medical Professional")) {
            roles = [...roles, "Medical Professional"];
          }

          const isStaffAccount = userData.accountType === "Staff";
          const isFullMedicalProfessional = isStaffAccount && (
            userData.role === "Medical Professional" || roles.includes("Medical Professional")
          );
          const hasAssignedServices = isStaffAccount && (
            userData.servicesAssigned === true || userData.servicesassigned === true
          );
          const assignServices = Array.isArray(userData.assignServices) ? userData.assignServices : [];

          setStaffProfile(userData);
          setCanReviewAppointments(isStaffAccount && (isFullMedicalProfessional || (hasAssignedServices && assignServices.length > 0)));
          setUserRoles(roles);

          // Use centralized role configurations
          const filteredRoles = roles
            .filter(roleId => roleConfigs[roleId])
            .map(roleId => ({
              id: roleId,
              name: roleConfigs[roleId].name,
              component: roleConfigs[roleId].component
            }));

          setAvailableRoles(filteredRoles);
          
          // Set current role based on current page or first available role
          const currentPath = window.location.pathname;
          let currentRole = "";
          
          if (currentPath.includes("/medical-professional")) {
            currentRole = "Medical Professional";
          } else if (currentPath.includes("/peer-educator")) {
            currentRole = "Peer Educator";
          } else if (currentPath.includes("/content-curator")) {
            currentRole = "Content Curator";
          } else {
            // Default to first available role if not on a staff page
            currentRole = filteredRoles.length > 0 ? filteredRoles[0].id : "";
          }
          
          setCurrentUserRole(currentRole);
          if (userData && roles.includes("Medical Professional") && userData.passwordChanged !== true) {
            setShowPasswordChangeModal(true);
          }
          
          // Check onboarding status
          if (roles.includes("Medical Professional")) {
            if (userData.passwordChanged !== true) {
              setShowPasswordChangeModal(true);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching user roles:', error);
      }
    };
    
    fetchUserRoles();
  }, []);

  // Listen for authentication changes
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        // Trigger role fetching when user becomes authenticated
        const fetchRoles = async () => {
          try {
            const userDoc = await getDoc(doc(db, 'users', user.uid));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              let roles = userData.roles || [];

              if (userData.role === "Medical Professional" && !roles.includes("Medical Professional")) {
                roles = [...roles, "Medical Professional"];
              }
              if (roles.includes("Peer Educator") && 
                  (userData.servicesAssigned === true || userData.servicesassigned === true) && 
                  !roles.includes("Medical Professional")) {
                roles = [...roles, "Medical Professional"];
              }

              const isStaffAccount = userData.accountType === "Staff";
              const isFullMedicalProfessional = isStaffAccount && (
                userData.role === "Medical Professional" || roles.includes("Medical Professional")
              );
              const hasAssignedServices = isStaffAccount && (
                userData.servicesAssigned === true || userData.servicesassigned === true
              );
              const assignServices = Array.isArray(userData.assignServices) ? userData.assignServices : [];

              setStaffProfile(userData);
              setCanReviewAppointments(isStaffAccount && (isFullMedicalProfessional || (hasAssignedServices && assignServices.length > 0)));
              setUserRoles(roles);
              
              // Use centralized role configurations
              const filteredRoles = roles
                .filter(roleId => roleConfigs[roleId])
                .map(roleId => ({
                  id: roleId,
                  name: roleConfigs[roleId].name,
                  component: roleConfigs[roleId].component
                }));
              
              setAvailableRoles(filteredRoles);
              
              // Set current role based on current page or first available role
              const currentPath = window.location.pathname;
              let currentRole = "";
              
              if (currentPath.includes("/medical-professional")) {
                currentRole = "Medical Professional";
              } else if (currentPath.includes("/peer-educator")) {
                currentRole = "Peer Educator";
              } else if (currentPath.includes("/content-curator")) {
                currentRole = "Content Curator";
              } else {
                currentRole = filteredRoles.length > 0 ? filteredRoles[0].id : "";
              }
              
              setCurrentUserRole(currentRole);
              if (userData && roles.includes("Medical Professional") && userData.passwordChanged !== true) {
                setShowPasswordChangeModal(true);
              }
            }
          } catch (error) {
            console.error('Error fetching user roles (auth listener):', error);
          }
        };
        
        fetchRoles();
      }
    });

    return () => unsubscribe();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (switchButtonRef.current && !switchButtonRef.current.contains(event.target) && 
          !event.target.closest('.mp-sidebar-role-dropdown')) {
        setShowRoleDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    let unsubscribeConfirmed = () => {};
    let unsubscribeAssigned = () => {};
    let unsubscribeNotifs = () => {};
    let mounted = true;

    const setupSubscription = () => {
      const user = auth.currentUser;
      if (!user) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      let confirmedAppointments = [];
      let assignedAppointments = [];

      const updateAppointmentLists = () => {
        const uniqueUpcoming = Array.from(new Map(
          [...confirmedAppointments, ...assignedAppointments]
            .filter(a => ["CONFIRMED", "ACCEPTED"].includes(a.status))
            .map(item => [item.id, item])
        ).values());

        setUpcomingAppointments(uniqueUpcoming);
        setIsLoading(false);
      };

      unsubscribeConfirmed = appointmentService.subscribeToConfirmedAppointments((appointments) => {
        if (!mounted) return;
        confirmedAppointments = appointments;
        updateAppointmentLists();
      });

      unsubscribeAssigned = appointmentService.subscribeToProAppointments(user.uid, (appointments) => {
        if (!mounted) return;
        assignedAppointments = appointments.filter(appointment => appointment.status === "ACCEPTED");
        setHistoryAppointments(
          appointments.filter(appointment =>
            ["COMPLETED", "CANCELLED_BY_USER", "CANCELLED_BY_PRO"].includes(appointment.status)
          )
        );
        updateAppointmentLists();
      });
    };

    setupSubscription();

    // Listen for professional notifications - OPTIMIZED
    const setupNotifSubscription = () => {
      // Access status is tracked per request in dataAccessRequests and is
      // surfaced through the persistent notification subscription.
      unsubscribeNotifs = () => {};
    };

    setupNotifSubscription();

    return () => {
      mounted = false;
      if (unsubscribeConfirmed) unsubscribeConfirmed();
      if (unsubscribeAssigned) unsubscribeAssigned();
      if (unsubscribeNotifs) unsubscribeNotifs();
    };
  }, [auth.currentUser]); // Re-run if user changes or becomes available

  useEffect(() => {
    let unsubscribe = () => {};

    const setupPendingReviewSubscription = () => {
      const user = auth.currentUser;
      if (!user || !staffProfile) {
        setReviewAppointments([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      if (!canReviewAppointments) {
        setReviewAppointments([]);
        setIsLoading(false);
        return;
      }

      unsubscribe = appointmentService.subscribeToPendingAppointmentsForStaff(staffProfile, (all) => {
        const uniqueAll = Array.from(new Map(all.map(item => [item.id, item])).values());
        setReviewAppointments(uniqueAll);
        setIsLoading(false);
      });
    };

    setupPendingReviewSubscription();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [auth.currentUser, staffProfile, canReviewAppointments]);

  const handleApproveToUpcoming = async (appointment) => {
    try {
      const additionalData = {};
      const currentUser = auth.currentUser;
      if (currentUser) {
        additionalData.proId = null;
        additionalData.proName = null;
        additionalData.auditApprovedId = currentUser.uid;
      }

      await appointmentService.updateStatus(appointment.id, "CONFIRMED", additionalData);
      // Local state update removed. Real-time listener will handle this.
    } catch (err) {
      setNotification({ message: "Failed to approve appointment.", type: "error" });
    }
  };

  const handleRefreshReviewAppointments = async () => {
    if (!canReviewAppointments || !staffProfile) return;
    setIsLoading(true);
    try {
      const all = await appointmentService.getPendingAppointmentsForStaff(staffProfile);
      const uniqueAll = Array.from(new Map(all.map(item => [item.id, item])).values());
      setReviewAppointments(uniqueAll);
    } catch (err) {
      console.error('Error refreshing review appointments:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompleteFromUpcoming = async (appointment) => {
    try {
      await appointmentService.updateStatus(appointment.id, "COMPLETED");
      // Local state update removed. Real-time listener will handle this.
    } catch (err) {
      setNotification({ message: "Failed to complete appointment.", type: "error" });
    }
  };

  const handleAcceptFromUpcoming = async (appointment) => {
    try {
      const currentUser = auth.currentUser;
      const additionalData = {};
      if (currentUser) {
        const proName = staffProfile?.fullName || `${staffProfile?.firstName || ''} ${staffProfile?.lastName || ''}`.trim() || currentUser.displayName || "Medical Professional";
        additionalData.proId = currentUser.uid;
        additionalData.proName = proName;
        additionalData.auditAcceptedId = currentUser.uid;
      }
      await appointmentService.updateStatus(appointment.id, "ACCEPTED", additionalData);
      // Real-time listener will handle state updates.
    } catch (err) {
      setNotification({ message: "Failed to accept appointment.", type: "error" });
    }
  };

  const handleRefreshUpcomingAppointments = async () => {
    setIsLoading(true);
    try {
      const user = auth.currentUser;
      if (!user) return;
      const [confirmed, assigned] = await Promise.all([
        appointmentService.getConfirmedAppointments(),
        appointmentService.getProAppointments(user.uid)
      ]);
      const uniqueAll = Array.from(new Map(
        [
          ...confirmed,
          ...assigned.filter(appointment => appointment.status === "ACCEPTED")
        ].map(item => [item.id, item])
      ).values());
      const upcoming = uniqueAll.filter(a => ["CONFIRMED", "ACCEPTED"].includes(a.status));
      setUpcomingAppointments(upcoming);
    } catch (err) {
      console.error('Error refreshing upcoming appointments:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefreshHistoryAppointments = async () => {
    setIsLoading(true);
    try {
      const user = auth.currentUser;
      if (!user) return;
      const all = await appointmentService.getProAppointments(user.uid);
      const uniqueAll = Array.from(new Map(all.map(item => [item.id, item])).values());
      const history = uniqueAll.filter(a => ["COMPLETED", "CANCELLED_BY_USER", "CANCELLED_BY_PRO"].includes(a.status));
      setHistoryAppointments(history);
    } catch (err) {
      console.error('Error refreshing history appointments:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const renderPanel = () => {
    if (activePanel === "dashboard") {
      return (
        <MedicalDashboard
          setShowWalkinModal={setShowWalkinModal}
          staffProfile={staffProfile}
          canReviewAppointments={canReviewAppointments}
        />
      );
    }

    if (isLoading) {
      return <div className="loading-container">{t.loading} appointments...</div>;
    }
    switch (activePanel) {
      case "review":
        if (!canReviewAppointments) {
          return (
            <div className="loading-container" style={{ textAlign: 'center', padding: '32px' }}>
              <h3>Medical Professional review access is unavailable.</h3>
              <p>
                Only staff accounts with Medical Professional privileges or assigned service access can view pending appointments.
              </p>
            </div>
          );
        }
        return (
          <ReviewAppointments
            appointments={reviewAppointments}
            setAppointments={setReviewAppointments}
            onApprove={handleApproveToUpcoming}
            refreshAppointments={handleRefreshReviewAppointments}
            staffProfile={staffProfile}
            setShowWalkinModal={setShowWalkinModal}
            sidebarCollapsed={sidebarCollapsed}
          />
        );
      case "upcoming":
        return (
          <UpcomingAppointments
            appointments={upcomingAppointments}
            onComplete={handleCompleteFromUpcoming}
            onApprove={handleApproveToUpcoming}
            onAccept={handleAcceptFromUpcoming}
            refreshAppointments={handleRefreshUpcomingAppointments}
            staffProfile={staffProfile}
            sidebarCollapsed={sidebarCollapsed}
          />
        );
      case "history":
        return <AppointmentHistory appointments={historyAppointments} staffProfile={staffProfile} refreshAppointments={handleRefreshHistoryAppointments} sidebarCollapsed={sidebarCollapsed} />;
      case "records":
        return (
          <MedicalRecords
            appointments={historyAppointments}
            staffProfile={staffProfile}
            refreshAppointments={handleRefreshHistoryAppointments}
            sidebarCollapsed={sidebarCollapsed}
            highlightState={highlightState}
          />
        );
      case "notifications":
        return (
          <ProNotifications
            role="medical"
            notifications={notifications}
            onUnreadCountChange={setUnreadNotificationsCount}
            onNotificationClick={(patientUid, appointmentId) => {
              setHighlightState({
                highlightPatientUid: patientUid,
                highlightAppointmentId: appointmentId
              });
              setActivePanel('records');
            }}
          />
        );
      case "escalatedCases":
        return <MedicalEscalatedCases />;
      case "profile":
        return <MedicalProfile />;
      default:
        return <MedicalDashboard />;
    }
  };

  const handleLogout = () => {
    setIsLogoutModalOpen(true);
  };

  const handlePanelSelect = (panel) => {
    setActivePanel(panel);
    setIsMobileDrawerOpen(false);
  };

  return (
    <div className="mp-layout">
      {/* ── Custom Tooltip for Collapsed Sidebar ── */}
      {tooltip.visible && isDesktop && sidebarCollapsed && (
        <div
          className="mp-custom-tooltip"
          style={{
            left: tooltip.x,
            top: tooltip.y
          }}
        >
          {tooltip.text}
        </div>
      )}

      {/* ── Mobile backdrop ── */}
      {isMobileDrawerOpen && (
        <div
          className="mp-drawer-backdrop visible"
          onClick={() => setIsMobileDrawerOpen(false)}
        />
      )}

      <aside className={`mp-sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${isMobileDrawerOpen ? 'drawer-open' : ''}`}>
        {/* Mobile sidebar header (compact navbar-style branding) */}
        <div className="mp-sidebar-mobile-header">
          <div className="mp-sidebar-mobile-logos">
            <img src={logoName} alt="AuraVit" />
            <span className="mp-sidebar-logo-sep">|</span>
            <img src={cfsiLogo} alt="CFSI" />
          </div>
          <button
            className="mp-drawer-close"
            onClick={() => setIsMobileDrawerOpen(false)}
            aria-label="Close menu"
          >
            <FiX />
          </button>
        </div>

        {/* Desktop brand section */}
        <div 
          className="mp-brand"
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        >
          <img
            src={sidebarCollapsed ? auravitIcon : logoName}
            alt="AURAVIT"
            className="mp-sidebar-logo"
          />
          {!sidebarCollapsed && (
            <img
              src={cfsiLogo}
              alt="CFSI"
              className="mp-cfsi-logo"
            />
          )}
        </div>

        <div className="mp-sidebar-content">
          <nav className="mp-nav">
          <button
            className={activePanel === "dashboard" ? "active" : ""}
            onClick={() => handlePanelSelect("dashboard")}
            onMouseEnter={(e) => handleMouseEnter(e, t.dashboard)}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            aria-label={t.dashboard}
          >
            <FiGrid /> <span>{t.dashboard}</span>
          </button>
          <button
            className={activePanel === "review" ? "active" : ""}
            onClick={() => handlePanelSelect("review")}
            onMouseEnter={(e) => handleMouseEnter(e, "Review Appointments")}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            aria-label="Review Appointments"
            style={{ position: 'relative' }}
          >
            <FiCalendar /> <span>Review<br />Appointments</span>
            {reviewAppointments.length > 0 && (
              <span style={{
                position: 'absolute',
                top: '10px',
                right: sidebarCollapsed ? '8px' : '16px',
                minWidth: '22px',
                height: '22px',
                padding: '0 6px',
                borderRadius: '999px',
                background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
                color: '#fff',
                fontSize: '11px',
                fontWeight: 800,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 1,
                border: '2px solid #fff',
                boxShadow: '0 4px 8px rgba(239, 68, 68, 0.25)'
              }}>
                {reviewAppointments.length > 99 ? '99+' : reviewAppointments.length}
              </span>
            )}
          </button>
          <button
            className={activePanel === "upcoming" ? "active" : ""}
            onClick={() => handlePanelSelect("upcoming")}
            onMouseEnter={(e) => handleMouseEnter(e, "Upcoming Appointments")}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            aria-label="Upcoming Appointments"
            style={{ position: 'relative' }}
          >
            <MdOutlineUpcoming /> <span>Upcoming Appointments</span>
            {upcomingAppointments.length > 0 && (
              <span style={{
                position: 'absolute',
                top: '10px',
                right: sidebarCollapsed ? '8px' : '16px',
                minWidth: '22px',
                height: '22px',
                padding: '0 6px',
                borderRadius: '999px',
                background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
                color: '#fff',
                fontSize: '11px',
                fontWeight: 800,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 1,
                border: '2px solid #fff',
                boxShadow: '0 4px 8px rgba(239, 68, 68, 0.25)'
              }}>
                {upcomingAppointments.length > 99 ? '99+' : upcomingAppointments.length}
              </span>
            )}
          </button>
          <button
            className={activePanel === "history" ? "active" : ""}
            onClick={() => handlePanelSelect("history")}
            onMouseEnter={(e) => handleMouseEnter(e, "Appointment History")}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            aria-label="Appointment History"
          >
            <MdHistory /> <span>Appointment History</span>
          </button>
          <button
            className={activePanel === "records" ? "active" : ""}
            onClick={() => handlePanelSelect("records")}
            onMouseEnter={(e) => handleMouseEnter(e, "Records")}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            aria-label="Records"
          >
            <MdDescription /> <span>Records</span>
          </button>
          <button
            className={activePanel === "escalatedCases" ? "active" : ""}
            onClick={() => handlePanelSelect("escalatedCases")}
            onMouseEnter={(e) => handleMouseEnter(e, t.escalatedCases)}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            aria-label={t.escalatedCases}
          >
            <MdAssignment /> <span>{t.escalatedCases}</span>
          </button>
          <button
            className={activePanel === "notifications" ? "active" : ""}
            onClick={() => {
              handlePanelSelect("notifications");
            }}
            style={{ position: 'relative' }}
            onMouseEnter={(e) => handleMouseEnter(e, "Notifications")}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            aria-label="Notifications"
          >
            <MdNotificationsNone /> <span>Notifications</span>
            {unreadNotificationsCount > 0 && (
              <span style={{
                position: 'absolute',
                top: '10px',
                right: sidebarCollapsed ? '8px' : '16px',
                minWidth: '22px',
                height: '22px',
                padding: '0 6px',
                borderRadius: '999px',
                background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
                color: '#fff',
                fontSize: '11px',
                fontWeight: 800,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 1,
                border: '2px solid #fff',
                boxShadow: '0 4px 8px rgba(239, 68, 68, 0.25)'
              }}>
                {unreadNotificationsCount > 99 ? '99+' : unreadNotificationsCount}
              </span>
            )}
          </button>
          <button
            className={activePanel === "profile" ? "active" : ""}
            onClick={() => handlePanelSelect("profile")}
            onMouseEnter={(e) => handleMouseEnter(e, t.profile)}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            aria-label={t.profile}
          >
            <FiUser /> <span>{t.profile}</span>
          </button>
          <button
            className="mp-logout"
            onClick={handleLogout}
            onMouseEnter={(e) => handleMouseEnter(e, "Logout")}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            aria-label="Logout"
          >
            <FiLogOut /> <span>Logout</span>
          </button>
        </nav>
        </div>

        {/* ================= Sidebar Bottom Actions ================= */}
        <div className="mp-sidebar-bottom">
          <div className="mp-sidebar-bottom-row">
            {/* Switch Account Button */}
            <div className="mp-sidebar-switch">
              <button
                ref={switchButtonRef}
                className="mp-sidebar-switch-btn"
                onClick={handleDropdownToggle}
                onMouseEnter={(e) => handleMouseEnter(e, "Switch Account")}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
                aria-label="Switch Account"
              >
                <HiOutlineSwitchVertical />
                <span>Switch Account</span>
                <FiChevronDown className={`mp-chevron ${showRoleDropdown ? 'open' : ''}`} />
              </button>

              {showRoleDropdown && (
                <div 
                  className="mp-sidebar-role-dropdown"
                  style={sidebarCollapsed && isDesktop ? {
                    position: 'fixed',
                    top: `${dropdownPosition.top}px`,
                    left: `${dropdownPosition.left}px`
                  } : {}}
                >
                  {availableRoles.length > 0 ? (
                    availableRoles.map((role) => (
                      <button
                        key={role.id}
                        className={`mp-sidebar-role-option ${role.id === currentUserRole ? "active" : ""}`}
                        onClick={() => handleRoleSwitch(role)}
                      >
                        {role.name}
                        {role.id === currentUserRole && <span className="mp-current-indicator">✓</span>}
                      </button>
                    ))
                  ) : (
                    <div className="mp-sidebar-role-option" style={{ color: '#999', cursor: 'default' }}>
                      No roles available
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </aside>

      {/* ── Mobile top header ── */}
      <header className="mp-mobile-header">
        <div className="mp-mobile-header-logos">
          <img src={logoName} alt="AuraVit" />
          <span className="mp-logo-sep">|</span>
          <img src={cfsiLogo} alt="CFSI" />
        </div>
        <button
          className="mp-mobile-hamburger"
          onClick={() => setIsMobileDrawerOpen(true)}
          aria-label="Open menu"
        >
          <FiMenu />
        </button>
      </header>

      <section className="mp-main">
        <div className="mp-workspace">{renderPanel()}</div>
      </section>
      <StaffLogoutModal
        isOpen={isLogoutModalOpen}
        onClose={() => setIsLogoutModalOpen(false)}
      />

      {showWalkinModal && (
        <WalkinModal 
          onClose={() => setShowWalkinModal(false)} 
          onCreated={(newAppt) => {
            // Local state update removed. Real-time listener will handle the new appointment.
            setShowWalkinModal(false);
          }}
          setNotification={setNotification}
        />
      )}

      <NotificationModal 
        message={notification.message} 
        type={notification.type} 
        onClose={() => setNotification({ message: "", type: "" })} 
      />

      {/* Mandatory Password Change Modal */}
      {showPasswordChangeModal && createPortal(
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.85)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 11000,
          padding: '20px'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '16px',
            overflow: 'hidden',
            maxWidth: '600px',
            width: '100%',
            boxShadow: '0 25px 65px rgba(15, 23, 42, 0.45)',
            animation: 'scpModalFadeIn 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            <StaffChangePassword userData={userData} isMandatory={true} onPasswordVerificationRequired={handlePasswordVerificationRequired} />
          </div>
        </div>,
        document.body
      )}

      {/* Password Verification Modal */}
      {showPasswordVerificationModal && createPortal(
        <StaffChangeVerificationModal
          isOpen={showPasswordVerificationModal}
          onClose={() => setShowPasswordVerificationModal(false)}
          flow="password-change"
          pendingPasswordChange={pendingPasswordChange}
          onSuccess={handlePasswordVerificationSuccess}
          onCancel={handlePasswordVerificationCancel}
        />,
        document.body
      )}
    </div>
  );
};

export default MedicalProfessional;
